import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Copy, RefreshCw, Server } from 'lucide-react';
import { toast } from 'sonner';

import useDesktopObservation from '@/hooks/useDesktopObservation';
import useDesktopCommand from '@/hooks/useDesktopCommand';
import { Button } from '@/components/ui/button';
import { formatDateTimeWithZone } from '@/utils/dateUtils';
import DeviceStatusSlot from './DeviceStatusSlot';
import DeviceActionCard from './DeviceActionCard';
import SupportDetails from './SupportDetails';

function presentation(status) {
  switch (status?.state) {
    case 'ready':
      // Not "Ready": the status panel already shows a READY tone label above
      // this title, and the two stacked read as the same word twice. This
      // matches what the tab list says for the same state (TCA-26100804).
      return {
        label: 'Services running',
        detail: 'This computer’s protected offline services are running.',
        className: 'border-emerald-200 bg-emerald-50',
        Icon: CheckCircle2,
      };
    case 'starting':
      return {
        label: 'Starting',
        detail: 'Protected offline services are still preparing.',
        className: 'border-amber-200 bg-amber-50',
        Icon: RefreshCw,
      };
    case 'recovering':
      return {
        label: 'Recovering',
        detail: 'The desktop is making one bounded attempt to restart its offline services.',
        className: 'border-amber-200 bg-amber-50',
        Icon: RefreshCw,
      };
    case 'rebuild_required':
      return {
        label: 'Administrator recovery required',
        detail: 'The protected store requires the separate administrator recovery process. An ordinary retry will not replace it.',
        className: 'border-red-300 bg-red-50',
        Icon: AlertTriangle,
      };
    default:
      return {
        label: 'Unavailable',
        detail: status?.reason === 'local_process_stop_unconfirmed'
          ? 'Stopping offline services could not be confirmed, so restart is paused. Retry to check again; online work may continue.'
          : 'Offline services are unavailable on this computer. Online work may continue while an administrator investigates.',
        className: 'border-red-300 bg-red-50',
        Icon: AlertTriangle,
      };
  }
}

// Overview's status answers the overall question, so it stays visible even
// when a page-wide problem is showing: it is that problem's own subject.
const TONE_FOR = Object.freeze({
  ready: 'green', starting: 'quiet', recovering: 'amber',
  unavailable: 'red', rebuild_required: 'red',
});

export default function LocalRuntimeCard({ shopTimezone = null, runtime = null }) {
  const ownRuntime = useDesktopObservation('getLocalRuntimeStatus', 'onLocalRuntimeStatusChanged', { enabled: !runtime });
  const observation = runtime || ownRuntime;
  const admin = useDesktopObservation('getLocalRuntimeAdminStatus', 'onLocalRuntimeStatusChanged', {
    invalidateEvent: true, validate: value => value?.ok === true && typeof value.state === 'string',
  });
  const command = useDesktopCommand('recovery');
  const desktop = command.scope.bridge;
  const status = observation.status;
  const details = admin.status;
  const view = useMemo(() => observation.loading
    ? { label: 'Checking', detail: 'Reading offline service status.', Icon: RefreshCw }
    : presentation(status && { ...status, reason: details?.state === status.state ? details.reason : null }), [status, details, observation.loading]);
  const retry = async () => {
    if (!observation.current() || status?.state !== 'unavailable' || !desktop?.retryLocalRuntime) return;
    const accept = observation.capture();
    await command.run(bridge => bridge.retryLocalRuntime(), async (result, error, current) => {
      if (!error && result?.ok !== false && typeof result?.state === 'string') accept({ state: result.state });
      if (error) toast.error('Restart outcome could not be confirmed. Check service status before another attempt.');
      else if (result?.ok !== false && result?.state === 'ready') toast.success('Offline services restarted');
      else toast.error(result?.reason === 'administrator_required'
        ? 'Administrator access is required' : 'Offline services could not be restarted');
      if (current()) await admin.refresh();
    });
  };

  const copyLocation = async () => {
    if (!command.scope.current() || !command.scope.admin || !admin.current() || !details?.diagnosticsDirectory) return;
    try {
      await navigator.clipboard.writeText(details.diagnosticsDirectory);
      if (command.scope.current()) toast.success('Diagnostic location copied');
    } catch {
      if (command.scope.current()) toast.error('Diagnostic location could not be copied');
    }
  };

  if (!desktop) return null;
  return (
    <div className="space-y-3" data-testid="local-runtime-card">
      <DeviceStatusSlot
        tone={TONE_FOR[status?.state] || 'quiet'}
        title={view.label}
        detail={view.detail}
        titleTestId="local-runtime-label"
        detailTestId="local-runtime-detail"
        testId="local-runtime-slot"
        ignoreOverride
      />

      {command.uncertain && <p role="status">The restart outcome is unknown. Check diagnostics before another attempt.</p>}
      <DeviceActionCard
        icon={<Server className="h-4 w-4" />}
        title="Offline services"
        description="The protected services this computer uses to work without a connection."
        testId="local-runtime-services-card"
      >
        <div className="flex flex-wrap gap-2">
          {details?.diagnosticsDirectory && (
            <Button variant="outline" size="sm" onClick={copyLocation} className="h-9 text-xs" data-testid="local-runtime-copy-diagnostics">
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Copy diagnostics location
            </Button>
          )}
          {status?.state === 'unavailable' && (
            <Button variant="outline" size="sm" onClick={retry} disabled={command.busy || !command.scope.admin} className="h-9 text-xs" data-testid="local-runtime-retry">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              {command.busy ? 'Pending' : 'Retry'}
            </Button>
          )}
        </div>
      </DeviceActionCard>

      <SupportDetails
        testId="local-runtime-support-details"
        rows={[
          ['Desktop version', typeof desktop.appVersion === 'string' && desktop.appVersion.trim()
            ? desktop.appVersion.trim() : null, 'local-runtime-app-version'],
          ['Last failure', details?.lastFailureAt
            ? `${formatDateTimeWithZone(details.lastFailureAt, shopTimezone) || 'Unknown'}${details.lastFailure?.process ? ` · ${details.lastFailure.process}` : ''}`
            : null, 'local-runtime-last-failure'],
        ]}
      />
    </div>
  );
}
